import type { UserProfile } from '../../../contexts/AuthContext';

interface Props {
  profile: UserProfile;
  onComplete: () => void;
}

interface ProfileField {
  label: string;
  filled: boolean;
  weight: number;
}

export const calculateProfileCompleteness = (profile: UserProfile): { pct: number; missing: string[] } => {
  const fields: ProfileField[] = [
    { label: 'Nome', filled: !!((profile.first_name || profile.name) && (profile.first_name || profile.name) !== ''), weight: 10 },
    { label: 'Sobrenome', filled: !!(profile.last_name && profile.last_name.trim()), weight: 10 },
    { label: 'Telefone', filled: !!(profile.phone && profile.phone.trim()), weight: 10 },
    { label: 'CPF', filled: !!(profile.cpf && profile.cpf.trim()), weight: 15 },
    { label: 'Foto de perfil', filled: !!(profile.avatar_url), weight: 5 },
    { label: 'CEP', filled: !!(profile.cep && profile.cep.trim()), weight: 10 },
    { label: 'Endereço', filled: !!(profile.street && profile.street.trim()), weight: 10 },
    { label: 'Número', filled: !!(profile.street_number && profile.street_number.trim()), weight: 5 },
    { label: 'Bairro', filled: !!(profile.neighborhood && profile.neighborhood.trim()), weight: 5 },
    { label: 'Cidade', filled: !!(profile.city && profile.city.trim()), weight: 10 },
    { label: 'Estado', filled: !!(profile.state && profile.state.trim()), weight: 10 },
  ];

  const totalWeight = fields.reduce((a, f) => a + f.weight, 0);
  const filledWeight = fields.filter(f => f.filled).reduce((a, f) => a + f.weight, 0);
  const pct = Math.round((filledWeight / totalWeight) * 100);
  const missing = fields.filter(f => !f.filled).map(f => f.label);

  return { pct, missing };
};

export const ProfileCompletenessCard = ({ profile, onComplete }: Props) => {
  const { pct, missing } = calculateProfileCompleteness(profile);

  if (pct >= 100) return null;

  const isLow = pct < 40;
  const isMedium = pct >= 40 && pct < 80;

  const color = isLow
    ? { bg: 'bg-red-50', border: 'border-red-200', bar: 'bg-red-400', text: 'text-red-700', icon: 'text-red-500' }
    : isMedium
    ? { bg: 'bg-amber-50', border: 'border-amber-200', bar: 'bg-amber-400', text: 'text-amber-700', icon: 'text-amber-500' }
    : { bg: 'bg-emerald-50', border: 'border-emerald-200', bar: 'bg-emerald-400', text: 'text-emerald-700', icon: 'text-emerald-500' };

  return (
    <div className={`rounded-2xl border-2 p-4 mb-4 ${color.bg} ${color.border}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 ${isLow ? 'bg-red-100' : isMedium ? 'bg-amber-100' : 'bg-emerald-100'}`}>
            <i className={`ri-user-settings-line text-base ${color.icon}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className={`text-sm font-700 ${color.text}`}>
                {pct < 40 ? 'Perfil muito incompleto' : pct < 80 ? 'Perfil parcialmente completo' : 'Quase lá!'}
              </p>
              <span className={`text-xs font-700 px-2 py-0.5 rounded-full ${isLow ? 'bg-red-100 text-red-700' : isMedium ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {pct}%
              </span>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-white/70 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full rounded-full transition-all duration-700 ${color.bar}`}
                style={{ width: `${pct}%` }}
              />
            </div>

            {missing.length > 0 && (
              <p className={`text-xs ${color.text} opacity-80`}>
                Faltando: {missing.slice(0, 4).join(', ')}{missing.length > 4 ? ` e mais ${missing.length - 4}` : ''}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={onComplete}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-700 cursor-pointer whitespace-nowrap transition-colors ${isLow ? 'bg-red-500 hover:bg-red-600 text-white' : isMedium ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}`}
        >
          <i className="ri-edit-line text-sm" />
          Completar agora
        </button>
      </div>
    </div>
  );
};
